import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Delete } from '@mui/icons-material';
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { ErrorBanner, FadingTooltip, UPLOAD_FILE_STATUS_KEY } from '@cosmotech/ui';
import { Api } from '../../../services/config/Api';
import { SCENARIO_RUN_STATE, SCENARIO_VALIDATION_STATUS } from '../../../services/config/ApiConstants';
import { ACL_PERMISSIONS } from '../../../services/config/accessControl';
import { TECHNICAL_POLICY_COLUMNS } from '../../../services/technicalPolicy/technicalPolicyColumns';
import { TwoActionsDialogService } from '../../../services/twoActionsDialog/twoActionsDialogService';
import { WizardUtils } from '../../../utils/WizardUtils';
import EditableTable from '../components/EditableTable';
import GenericAutoCompleteInput from '../components/GenericAutoCompleteInput';
import { useWizard } from './WizardHook';
import { NiveauPriorisationDialog } from './dialogs/NiveauPriorisationDialog';
import { ActionsImposeesTab } from './tabs/ActionsImposeesTab';
import ReglesGestionTab from './tabs/ReglesGestionTab';
import SequencesActionsTab from './tabs/SequencesActionsTab';
import { useActionsImposees } from './tabs/hooks/ActionsImposeesHook';

const gestionActivitesColumnsList = [
  'ReglesGestion',
  'CriteresDeclenchement',
  'Impacts',
  'Gisement',
  'ClasseActif',
  'id',
  'CoutsEtDureesActions',
  'SequencesActions',
];

const labels = {
  sauvegardeEnCours: 'Sauvegarde en cours...',
  gestionDesActivitesWizard: 'genericcomponent.technicalPolicy.common.activityManagement',
  niveauDePriorisation: 'genericcomponent.technicalPolicy.common.prioritizationLevelsButton',
  suivant: 'genericcomponent.common.next',
  sauvegarder: 'genericcomponent.common.save',
  annuler: 'genericcomponent.common.cancel',
  fermer: 'genericcomponent.common.close',
  reglesDeGestion: 'genericcomponent.technicalPolicy.tabs.managementRules',
  sequencesActions: 'genericcomponent.technicalPolicy.tabs.actionSequences',
  actionsImposees: 'genericcomponent.technicalPolicy.tabs.imposedActions',
};

const Wizard = ({
  isWizardOpen,
  handleCloseWizard,
  selectedGisement: initialGisement,
  selectedPolicy,
  activeTab: initialTab,
  initialStep,
}) => {
  const { t } = useTranslation();
  const methods = useForm({ defaultValues: {}, mode: 'onChange' });
  const { dirtyFields } = methods.formState;
  const [selectedGisement, setSelectedGisement] = useState(initialGisement || '');

  const {
    technicalPolicyData,
    getGisementData,
    datasetId,
    organizationId,
    scenarioData,
    saveData,
    assetClassesList,
    assetClassAttrsByGisement,
    setNewTableNamesMapping,
    userPermissionsOnCurrentScenario,
    gisements,
    updateGisementData,
    deleteGisement,
    status,
    assetClassAttributeValues,
    assetClassAttributesWithDescendants,
    assetClassAttributesValuesWithDescendants,
  } = useWizard(selectedGisement);
  const { getActionImposeesId } = useActionsImposees();

  const gisementsList = useMemo(() => {
    return WizardUtils.sortResourceListAlphabetically(Object.keys(gisements ?? {}));
  }, [gisements]);
  const [gisementOptions, setGisementOptions] = useState(gisementsList);
  const assetClassesNamesList = useMemo(() => {
    const niveauHierarchiqueList = assetClassesList?.map((assetClass) => assetClass.NiveauHierarchique);
    return WizardUtils.sortResourceListAlphabetically(niveauHierarchiqueList);
  }, [assetClassesList]);
  const assetClassByGisement = useMemo(() => {
    return technicalPolicyData?.gisements[selectedGisement]?.tableNamesMapping?.ClasseActif;
  }, [selectedGisement, technicalPolicyData]);
  const [selectedAssetClass, setSelectedAssetClass] = useState('');
  const assetClassAttributesParameter = useMemo(() => {
    const unparsedAssetClassAttributesParameter = scenarioData.parametersValues?.find(
      (parameter) => parameter.parameterId === 'asset_class_columns'
    );
    return unparsedAssetClassAttributesParameter?.value ? JSON.parse(unparsedAssetClassAttributesParameter.value) : {};
  }, [scenarioData.parametersValues]);
  const [selectedAssetClassAttrsList, setSelectedAssetClassAttrsList] = useState([]);
  const [selectedAssetClassAttrsValuesList, setSelectedAssetClassAttrsValuesList] = useState([]);
  const [selectedAssetClassNiveauCout, setSelectedAssetClassNiveauCout] = useState([]);
  const [loadingAssetClassAttributs, setLoadingAssetClassAttributs] = useState(false);
  const [deletedGisements, setDeletedGisements] = useState([]);

  const isDirty = Object.keys(dirtyFields ?? {}).length !== 0 || deletedGisements.length > 0;

  const generateGestionActivitesRow = () => {
    const newRow = {};
    gestionActivitesColumnsList.forEach((column) => {
      if (['Gisement', 'id'].includes(column)) {
        newRow[column] = selectedGisement;
      } else if (column === 'ClasseActif') newRow[column] = selectedAssetClass;
      else {
        newRow[column] = selectedGisement + column;
      }
    });
    return newRow;
  };

  const saveWizardData = async () => {
    const values = methods.getValues();
    const reset = methods.reset;

    const hasDeletedGisements = values._deletedGisements && values._deletedGisements.length > 0;

    if (selectedGisement && !Object.keys(technicalPolicyData?.gisements).includes(selectedGisement)) {
      const newGestionActivites = generateGestionActivitesRow();
      setNewTableNamesMapping(scenarioData.id, selectedGisement, newGestionActivites);
      updateGisementData(
        scenarioData.id,
        [{ Attrs: selectedAssetClassAttrsList, NiveauCout: selectedAssetClassNiveauCout }],
        'ClasseActif',
        selectedGisement
      );
      await saveData(values, reset, setSaveTechnicalPolicyError, newGestionActivites);
    } else if (hasDeletedGisements) {
      await saveData(values, reset, setSaveTechnicalPolicyError);

      deletedGisements.forEach((gisement) => {
        deleteGisement(scenarioData.id, gisement);
      });

      setDeletedGisements([]);
    } else {
      await saveData(values, reset, setSaveTechnicalPolicyError);
    }

    const currentActionsImposeesToUpload = { ...currentActionsImposees };
    if (currentActionsImposees?.selectedGisement != null) {
      if (currentActionsImposeesToUpload.currentFile.status === UPLOAD_FILE_STATUS_KEY.READY_TO_UPLOAD) {
        currentActionsImposeesToUpload.currentFile.status = UPLOAD_FILE_STATUS_KEY.READY_TO_DOWNLOAD;
        currentActionsImposeesToUpload.currentFile.name = getActionImposeesId(currentActionsImposees.selectedGisement);
      }
      setCurrentActionsImposees(currentActionsImposeesToUpload);
    }
  };

  const calculateClassActifAttributes = (classeActifAttrs) => {
    if (!classeActifAttrs) return;
    const attributsList = classeActifAttrs.filter(
      (attr) =>
        ![
          'AgeApparent',
          'AgeApparentConnu',
          'DateDerniereAction',
          'DateDerniereInspection',
          'DateInstallation',
          'FamilleEquipement',
          'id',
          'Identifiant',
        ].includes(attr)
    );
    return WizardUtils.sortResourceListAlphabetically(attributsList);
  };

  const assetClassComponent = useMemo(() => {
    if (assetClassByGisement)
      return (
        <TextField
          data-cy="asset-class"
          label={t('genericcomponent.technicalPolicy.common.assetClass', 'Asset Class')}
          value={assetClassByGisement}
          disabled
        />
      );
    else
      return (
        <FormControl>
          <InputLabel id="class-actif">
            {t('genericcomponent.technicalPolicy.common.assetClass', 'Asset Class')}
          </InputLabel>
          <Select
            data-cy="asset-class"
            sx={{ minWidth: 200 }}
            label={t('genericcomponent.technicalPolicy.common.assetClass', 'Asset Class')}
            labelId="class-actif"
            value={selectedAssetClass}
            onChange={async (e) => {
              try {
                setLoadingAssetClassAttributs(true);
                const { data: assetClassReducedData } = await Api.Datasets.twingraphQuery(organizationId, datasetId, {
                  query: WizardUtils.buildMetadataQueryString(e.target.value),
                });
                const assetClassAttributes = assetClassReducedData.find(
                  (values) => values.Metadata === 'AvailableColumns'
                ).Values;
                const assetClassNiveauCout =
                  assetClassReducedData.find((values) => values.Metadata === 'NiveauCout')?.Values || [];
                const assetClassAttributesValues = WizardUtils.getFilteredMetadata(assetClassReducedData);
                const assetClassAttributesList =
                  assetClassAttributesParameter?.[e.target.value]?.split(',') || assetClassAttributes;
                setSelectedAssetClass(e.target.value);
                setSelectedAssetClassAttrsList(assetClassAttributesList);
                setSelectedAssetClassNiveauCout(assetClassNiveauCout);
                setSelectedAssetClassAttrsValuesList(assetClassAttributesValues);
                setLoadingAssetClassAttributs(false);
              } catch (error) {
                console.error(error);
                setLoadingAssetClassAttributs(false);
              }
            }}
            options={assetClassesNamesList}
          >
            {assetClassesNamesList?.map((option) => (
              <MenuItem data-cy={option} key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
  }, [
    assetClassAttributesParameter,
    assetClassByGisement,
    assetClassesNamesList,
    datasetId,
    organizationId,
    selectedAssetClass,
    t,
  ]);
  const [activeStep, setActiveStep] = useState(initialStep || 0);
  const [activeTab, setActiveTab] = useState(initialTab || 0);
  const [selectedRegle, setSelectedRegle] = useState(selectedPolicy || '');
  const [selectedSequenceAction, setSelectedSequenceAction] = useState('');
  const [currentActionsImposees, setCurrentActionsImposees] = useState(undefined);
  const hasUserWritePermission = userPermissionsOnCurrentScenario.includes(ACL_PERMISSIONS.SCENARIO.WRITE);
  const isCurrentScenarioRunning =
    scenarioData?.state === SCENARIO_RUN_STATE.RUNNING ||
    scenarioData?.state === SCENARIO_RUN_STATE.DATA_INGESTION_IN_PROGRESS;
  const isCurrentScenarioRejected = scenarioData?.validationStatus === SCENARIO_VALIDATION_STATUS.REJECTED;
  const isCurrentScenarioValidated = scenarioData?.validationStatus === SCENARIO_VALIDATION_STATUS.VALIDATED;

  const editMode = !(
    isCurrentScenarioRunning ||
    isCurrentScenarioRejected ||
    isCurrentScenarioValidated ||
    !hasUserWritePermission
  );

  const handleNext = () => {
    if (selectedGisement) {
      if (!Object.keys(technicalPolicyData?.gisements).includes(selectedGisement)) {
        const newGestionActivites = generateGestionActivitesRow();
        setNewTableNamesMapping(scenarioData.id, selectedGisement, newGestionActivites);
        updateGisementData(
          scenarioData.id,
          [{ Attrs: selectedAssetClassAttrsList, NiveauCout: selectedAssetClassNiveauCout }],
          'ClasseActif',
          selectedGisement
        );
      } else if (Object.values(technicalPolicyData?.gisements[selectedGisement]).includes(null)) {
        const selectedGisementTablesMapping = technicalPolicyData?.gisements[selectedGisement].tableNamesMapping;
        if (!('ReglesGestion' in selectedGisementTablesMapping)) {
          const updatedMapping = {
            ...selectedGisementTablesMapping,
            ReglesGestion: selectedGisement + '_ReglesGestion',
            CriteresDeclenchement: selectedGisement + '_CriteresDeclenchement',
          };
          setNewTableNamesMapping(scenarioData.id, selectedGisement, updatedMapping);
        }
        getGisementData(selectedGisementTablesMapping, datasetId, scenarioData);
      }
    }

    setActiveStep(1);
    setActiveTab(1);
  };

  const handleTabChange = (event, newTab) => setActiveTab(newTab);

  const handleGisementChange = (newGisement) => {
    setSelectedGisement(newGisement);
  };

  useEffect(() => {
    if (Object.keys(technicalPolicyData?.gisements || {}).includes(selectedGisement)) {
      const assetClassAttributesList =
        assetClassAttributesParameter?.[assetClassByGisement]?.split(',') || assetClassAttrsByGisement;
      // remove CustomDataNumber.AgeReel
      setSelectedAssetClassAttrsList(assetClassAttributesList?.filter((attr) => attr !== 'CustomDataNumber.AgeReel'));
    }
  }, [
    assetClassAttributesParameter,
    assetClassByGisement,
    assetClassAttrsByGisement,
    selectedAssetClass,
    selectedGisement,
    technicalPolicyData?.gisements,
  ]);

  const isDisabledAttribute = WizardUtils.filterSpecificAttributes;

  const getAttributeValues = (row) => {
    return assetClassAttributeValues?.[row] || selectedAssetClassAttrsValuesList?.[row] || [];
  };

  const columns = TECHNICAL_POLICY_COLUMNS.Gisement(
    calculateClassActifAttributes(selectedAssetClassAttrsList),
    (row) => {
      return isDisabledAttribute(row.Attribut);
    },
    (row) => {
      return getAttributeValues(row.Attribut);
    }
  );

  const handleDiscardChanges = useCallback(
    async (event) => {
      if (isDirty) {
        event.stopPropagation();
        const reset = methods.reset;
        const dialogProps = {
          id: 'discard-wizard-changes',
          labels: {
            title: t('genericcomponent.common.quitWithoutSaving.title', 'Quit without saving?'),
            body: t('genericcomponent.common.quitWithoutSaving.body', 'All unsaved changes will be lost.'),
            button1: t('genericcomponent.common.quitWithoutSaving.returnToEditing', 'RETURN TO EDITING'),
            button2: t('genericcomponent.common.quitWithoutSaving.quit', 'QUIT'),
          },
        };
        const result = await TwoActionsDialogService.openDialog(dialogProps);
        if (result === 2) {
          reset({});
          setActiveStep(0);
          setActiveTab(1);
          setSelectedGisement('');
          setSelectedRegle('');
          setSelectedSequenceAction('');
          handleCloseWizard();
          setGisementOptions(gisementsList);
          setDeletedGisements([]);
          setCurrentActionsImposees(undefined);
        }
      } else {
        setActiveStep(0);
        setActiveTab(1);
        setSelectedGisement('');
        setSelectedRegle('');
        setSelectedSequenceAction('');
        setGisementOptions(gisementsList);
        setDeletedGisements([]);
        handleCloseWizard();
        setCurrentActionsImposees(undefined);
      }
    },
    [gisementsList, handleCloseWizard, isDirty, methods.reset, t]
  );

  const [isNiveauPriorisationDialogOpen, setNiveauPriorisationDialogOpen] = useState(false);
  const [saveTechnicalPolicyError, setSaveTechnicalPolicyError] = useState(null);
  const clearTechnicalPolicyError = () => {
    setSaveTechnicalPolicyError(null);
  };

  const handleDeleteGisement = useCallback(
    async (gisementToDelete) => {
      if (!gisementToDelete || !technicalPolicyData?.gisements?.[gisementToDelete]) return;

      const dialogProps = {
        id: 'delete-gisement-confirmation',
        labels: {
          title: t('genericcomponent.common.confirmDialogs.deleteAssetCluster.title', 'Delete asset cluster?'),
          body: t(
            'genericcomponent.common.confirmDialogs.deleteAssetCluster.body',
            'Management rules, action sequences and imposed actions linked to this asset cluster will also be deleted'
          ),
          button1: t('genericcomponent.common.cancel', 'CANCEL'),
          button2: t('genericcomponent.common.delete', 'DELETE'),
        },
      };

      const result = await TwoActionsDialogService.openDialog(dialogProps);

      if (result === 2) {
        try {
          setDeletedGisements((prev) => [...prev, gisementToDelete]);
          methods.setValue('_deletedGisements', [...deletedGisements, gisementToDelete]);

          if (selectedGisement === gisementToDelete) {
            setSelectedGisement('');
            setSelectedAssetClass('');
            setSelectedAssetClassAttrsList([]);
            setSelectedAssetClassNiveauCout([]);
          }

          setGisementOptions((prevOptions) => prevOptions.filter((g) => g !== gisementToDelete));
        } catch (error) {
          console.error('Error deleting gisement:', error);
          setSaveTechnicalPolicyError(error);
        }
      }
    },
    [technicalPolicyData, selectedGisement, methods, deletedGisements, t]
  );

  useEffect(
    () => {
      if (isWizardOpen && selectedGisement && initialStep === 1) {
        const gisementKeys = Object.keys(technicalPolicyData?.gisements ?? {});
        const selectedGisementData = technicalPolicyData?.gisements[selectedGisement];
        const selectedGisementTablesMapping = selectedGisementData?.tableNamesMapping;
        const needsInit =
          !gisementKeys.includes(selectedGisement) || Object.values(selectedGisementData ?? {}).includes(null);
        if (needsInit) {
          if (!gisementKeys.includes(selectedGisement)) {
            const newGestionActivites = generateGestionActivitesRow();
            setNewTableNamesMapping(scenarioData.id, selectedGisement, newGestionActivites);
            updateGisementData(
              scenarioData.id,
              [{ Attrs: selectedAssetClassAttrsList, NiveauCout: selectedAssetClassNiveauCout }],
              'ClasseActif',
              selectedGisement
            );
          } else if (selectedGisementTablesMapping) {
            getGisementData(selectedGisementTablesMapping, datasetId, scenarioData);
          }
        }
      } // Dependencies like `generateGestionActivitesRow` or `updateGisementData` are stable but not memoized,
      // and adding them causes the effect to run unnecessarily on every render.
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [isWizardOpen, selectedGisement, initialStep]
  );

  useEffect(() => {}, [deletedGisements]);

  return (
    <FormProvider {...methods} key={`form-${scenarioData.id}-wizard`}>
      <div>
        <Dialog
          data-cy="technical-policy-dialog"
          open={isWizardOpen}
          onClose={handleCloseWizard}
          maxWidth="xl"
          disableEscapeKeyDown={true}
          fullWidth
        >
          <ErrorBanner
            error={saveTechnicalPolicyError}
            clearErrors={clearTechnicalPolicyError}
            labels={{
              dismissButtonText: t('commoncomponents.banner.button.dismiss', 'Dismiss'),
              tooLongErrorMessage: t(
                'commoncomponents.banner.tooLongErrorMessage',
                'Detailed error message is too long to be displayed. ' +
                  'To read it, please use the COPY button and paste it in your favorite text editor.'
              ),
              secondButtonText: t('commoncomponents.banner.button.copy.label', 'Copy'),
              toggledButtonText: t('commoncomponents.banner.button.copy.copied', 'Copied'),
            }}
          />
          <DialogTitle>
            <Grid container sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                {t(labels.gestionDesActivitesWizard) + ' - Wizard'}
              </Typography>
              <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {activeStep >= 1 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FadingTooltip title={selectedGisement}>
                      <TextField
                        value={selectedGisement}
                        label={t('genericcomponent.technicalPolicy.common.assetCluster', 'Asset Cluster')}
                        disabled
                        sx={{ minWidth: 250, marginTop: 2 }}
                      />
                    </FadingTooltip>
                    <FadingTooltip title={assetClassByGisement}>
                      <TextField
                        value={assetClassByGisement}
                        label={t('genericcomponent.technicalPolicy.common.assetClass', 'Asset Class')}
                        disabled
                        sx={{ minWidth: 250, marginTop: 2 }}
                      />
                    </FadingTooltip>
                  </Box>
                )}
                <Button
                  data-cy="open-niveau-priorisation-dialog-button"
                  onClick={() => setNiveauPriorisationDialogOpen(true)}
                  variant="contained"
                  color="secondary"
                >
                  {t(labels.niveauDePriorisation)}
                </Button>
              </Grid>
            </Grid>
          </DialogTitle>
          <DialogContent sx={{ width: '100%', mt: 2, height: activeStep === 1 ? '90vh' : 'auto' }}>
            <NiveauPriorisationDialog
              editMode={editMode}
              open={isNiveauPriorisationDialogOpen}
              setOpen={() => setNiveauPriorisationDialogOpen(false)}
              saveData={saveData}
            />
            {activeStep === 0 && (
              <>
                <Grid container sx={{ display: 'flex', alignItems: 'center', gap: 2, paddingTop: 1 }}>
                  <GenericAutoCompleteInput
                    id="gisements-selector"
                    onChange={(e, newGisement) => {
                      handleGisementChange(newGisement);
                    }}
                    value={selectedGisement ?? ''}
                    onNewOption={(newGisement) => {
                      setSelectedGisement(newGisement);
                      setGisementOptions((prevOptions) => [...prevOptions, newGisement]);
                    }}
                    options={gisementOptions}
                    label={t(
                      'genericcomponent.technicalPolicy.common.selectOrCreateAssetCluster',
                      'Select or create an asset cluster'
                    )}
                    editMode={editMode}
                  />
                  <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {assetClassComponent}

                    {selectedGisement && editMode && technicalPolicyData?.gisements?.[selectedGisement] && (
                      <IconButton
                        data-cy="delete-gisement-button"
                        onClick={async () => await handleDeleteGisement(selectedGisement)}
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
                <Controller
                  name={selectedGisement ?? ''}
                  key={selectedGisement ?? ''}
                  render={({ field }) => {
                    const { value, onChange } = field;
                    return loadingAssetClassAttributs ? (
                      <Skeleton width="100%" animation="wave">
                        <EditableTable columns={[]} rows={[]} onChange={onChange} />
                      </Skeleton>
                    ) : (
                      <EditableTable
                        id="gisement"
                        columns={columns.filter((col) => col?.isInvisible !== true)}
                        rows={
                          value?.filter((row) => !Object.values(row).every((cell) => cell == null)) ??
                          technicalPolicyData?.gisements[selectedGisement]?.Gisement ??
                          []
                        }
                        editMode={editMode && !!selectedGisement && (!!assetClassByGisement || !!selectedAssetClass)}
                        onChange={onChange}
                      />
                    );
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                  <Button
                    data-cy="close-technical-policy-dialog-button"
                    onClick={handleDiscardChanges}
                    variant="outlined"
                    color="secondary"
                  >
                    {isDirty ? t(labels.annuler) : t(labels.fermer)}
                  </Button>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      data-cy="next-step-button"
                      disabled={
                        !(selectedGisement && (assetClassByGisement || selectedAssetClass)) ||
                        loadingAssetClassAttributs
                      }
                      onClick={handleNext}
                      variant="contained"
                      color="primary"
                    >
                      {t(labels.suivant)}
                    </Button>
                    {editMode && (
                      <Button
                        data-cy="save-technical-policy-parameters-button"
                        onClick={saveWizardData}
                        variant="contained"
                        color="primary"
                        disabled={
                          (!(selectedGisement && (assetClassByGisement || selectedAssetClass)) &&
                            deletedGisements.length === 0) ||
                          loadingAssetClassAttributs ||
                          !isDirty
                        }
                      >
                        {t(labels.sauvegarder)}
                      </Button>
                    )}
                  </Box>
                </Box>
              </>
            )}
            {activeStep === 1 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Tabs value={activeTab} onChange={handleTabChange} centered>
                  <Tab data-cy="actions-sequence" label={t(labels.sequencesActions)} />
                  <Tab data-cy="rules" label={t(labels.reglesDeGestion)} />
                  <Tab data-cy="imposed-actions-sequence" label={t(labels.actionsImposees)} />
                </Tabs>
                <Box
                  data-cy="wizard-tab-content"
                  sx={{
                    flexGrow: 1,
                    display: 'flex',
                    marginTop: 2,
                    paddingTop: 1,
                    overflowY: 'auto',
                  }}
                >
                  {activeTab === 0 && (
                    <SequencesActionsTab
                      selectedGisement={selectedGisement}
                      selectedSequenceAction={selectedSequenceAction}
                      setSelectedSequenceAction={setSelectedSequenceAction}
                      editMode={editMode}
                    />
                  )}
                  {activeTab === 1 && (
                    <ReglesGestionTab
                      selectedAssetClassAttrsList={assetClassAttributesWithDescendants}
                      assetClassAttributesValues={assetClassAttributesValuesWithDescendants}
                      selectedGisement={selectedGisement}
                      selectedRegle={selectedRegle}
                      setSelectedRegle={setSelectedRegle}
                      editMode={editMode}
                    />
                  )}
                  {activeTab === 2 && (
                    <ActionsImposeesTab
                      defaultSelectedGisement={selectedGisement}
                      gisements={gisementsList}
                      currentActionsImposees={currentActionsImposees}
                      setCurrentActionsImposees={setCurrentActionsImposees}
                      editMode={editMode}
                    />
                  )}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      data-cy="close-technical-policy-dialog-button"
                      onClick={handleDiscardChanges}
                      variant="outlined"
                      color="secondary"
                    >
                      {isDirty ? t(labels.annuler) : t(labels.fermer)}
                    </Button>
                  </Box>
                  <Box>
                    {editMode && (
                      <Button
                        data-cy="save-technical-policy-parameters-button"
                        disabled={!isDirty}
                        onClick={saveWizardData}
                        variant="contained"
                        color="primary"
                      >
                        {t(labels.sauvegarder)}
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          </DialogContent>
          <Backdrop open={status === 'SAVING'}>
            <CircularProgress data-cy="saving-technical-parameters-loading-spinner" color="inherit" />
            <Typography variant="h4">{labels.sauvegardeEnCours}</Typography>
          </Backdrop>
        </Dialog>
      </div>
    </FormProvider>
  );
};
Wizard.propTypes = {
  isWizardOpen: PropTypes.bool,
  handleCloseWizard: PropTypes.func,
  selectedGisement: PropTypes.string,
  selectedPolicy: PropTypes.string,
  activeTab: PropTypes.number,
  initialStep: PropTypes.number,
};
export default Wizard;
